import { useState, useEffect, useRef } from 'react';
import { Mic } from 'lucide-react';
import { toast } from 'sonner';

// Check browser support outside component to avoid re-checking
const getSpeechRecognition = () => {
    if (typeof window === 'undefined') return null;
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

export const VoiceInput = ({ 
    onTranscript, 
    className = "",
    disabled = false 
}) => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const recognitionRef = useRef(null);
    const SpeechRecognition = getSpeechRecognition();
    const isSupported = !!SpeechRecognition;

    useEffect(() => {
        if (!SpeechRecognition) return;

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
            setIsListening(true);
        };

        recognition.onresult = (event) => {
            let finalTranscript = '';
            let interimTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                if (result.isFinal) {
                    finalTranscript += result[0].transcript;
                } else {
                    interimTranscript += result[0].transcript;
                }
            }

            const currentTranscript = finalTranscript || interimTranscript;
            setTranscript(currentTranscript);
            
            if (finalTranscript && onTranscript) {
                onTranscript(finalTranscript);
            }
        };

        recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error);
            setIsListening(false);
            
            if (event.error === 'not-allowed') {
                toast.error('Microphone access denied. Please enable it in your browser settings.');
            } else if (event.error === 'no-speech') {
                toast.info('No speech detected. Try again.');
            } else if (event.error !== 'aborted') {
                toast.error('Voice recognition error. Please try again.');
            }
        };

        recognition.onend = () => {
            setIsListening(false);
            setTranscript('');
        };

        recognitionRef.current = recognition;

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, [onTranscript, SpeechRecognition]);

    const toggleListening = () => {
        if (!isSupported) {
            toast.error('Voice input not supported in this browser. Try Chrome or Safari.');
            return;
        }

        if (disabled) return;

        if (isListening) {
            recognitionRef.current?.stop();
        } else {
            setTranscript('');
            try {
                recognitionRef.current?.start();
            } catch (error) {
                // Already started, ignore
            }
        }
    };

    if (!isSupported) {
        return null;
    }

    return (
        <button
            type="button"
            onClick={toggleListening}
            disabled={disabled}
            className={`relative p-3 rounded-full transition-all ${
                isListening 
                    ? 'bg-red-500 text-white shadow-lg animate-pulse' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            title={isListening ? 'Stop listening' : 'Start voice input'}
            data-testid="voice-input-btn"
        >
            <Mic className="h-5 w-5" />
            
            {/* Listening indicator */}
            {isListening && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 rounded-full animate-ping" />
            )}
        </button>
    );
};

// Inline voice input that sits next to a text input
export const VoiceInputInline = ({ 
    value, 
    onChange, 
    placeholder = "Type or tap mic to speak...",
    className = "",
    inputClassName = "",
    disabled = false,
}) => {
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef(null);
    const inputRef = useRef(null);
    const SpeechRecognition = getSpeechRecognition();
    const isSupported = !!SpeechRecognition;

    useEffect(() => {
        if (!SpeechRecognition) return;

        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onstart = () => {
            setIsListening(true);
        };

        recognition.onresult = (event) => {
            let finalTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                if (result.isFinal) {
                    finalTranscript += result[0].transcript;
                }
            }

            if (finalTranscript) {
                // Append to existing value with a space
                const newValue = value ? `${value} ${finalTranscript}`.trim() : finalTranscript;
                onChange(newValue);
            }
        };

        recognition.onerror = (event) => {
            console.error('Speech recognition error:', event.error);
            setIsListening(false);
            
            if (event.error === 'not-allowed') {
                toast.error('Microphone access denied');
            } else if (event.error !== 'aborted' && event.error !== 'no-speech') {
                toast.error('Voice input error');
            }
        };

        recognition.onend = () => {
            setIsListening(false);
        };

        recognitionRef.current = recognition;

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, [value, onChange, SpeechRecognition]);

    const toggleListening = () => {
        if (!isSupported || disabled) return;

        if (isListening) {
            recognitionRef.current?.stop();
        } else {
            try {
                recognitionRef.current?.start();
            } catch (error) {
                // Already started
            }
        }
    };

    return (
        <div className={`relative flex items-center gap-2 ${className}`}>
            <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                className={`flex-1 h-12 px-4 rounded-xl border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                    isListening ? 'ring-2 ring-red-500 ring-offset-2' : ''
                } ${inputClassName}`}
                data-testid="voice-text-input"
            />
            
            {isSupported && (
                <button
                    type="button"
                    onClick={toggleListening}
                    disabled={disabled}
                    className={`flex-shrink-0 p-3 rounded-xl transition-all ${
                        isListening 
                            ? 'bg-red-500 text-white shadow-lg' 
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={isListening ? 'Stop' : 'Voice input'}
                    data-testid="voice-toggle-btn"
                >
                    {isListening ? (
                        <div className="relative">
                            <Mic className="h-5 w-5" />
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full animate-ping" />
                        </div>
                    ) : (
                        <Mic className="h-5 w-5" />
                    )}
                </button>
            )}
        </div>
    );
};

export default VoiceInput;
